import React from "react";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { OfflineProvider, useOffline } from "../../contexts/OfflineContext";
import { InitialsAvatar } from "../../components/shared/InitialsAvatar";
import { LayoutGrid, MapPin, CalendarRange, User, Wifi, WifiOff, RotateCw } from "lucide-react";
import { MOBILE } from "../../constants/testIds";

const tabs = [
    { to: "/m/today", label: "Today",   icon: LayoutGrid,    testid: MOBILE.tabToday, end: true },
    { to: "/m/jobs",  label: "Jobs",    icon: MapPin,        testid: MOBILE.tabJobs },
    { to: "/m/week",  label: "Week",    icon: CalendarRange, testid: MOBILE.tabWeek },
    { to: "/m/profile", label: "Profile", icon: User,        testid: MOBILE.tabProfile },
];

function OfflineBanner() {
    const { online, queued } = useOffline();
    if (online && queued === 0) return null;
    return (
        <div
            data-testid={MOBILE.offlineBanner}
            className="text-[13px] font-medium py-2 px-4 flex items-center gap-2 justify-center"
            style={{
                background: online ? "var(--km-yellow-l)" : "var(--km-yellow-l)",
                color: "var(--km-yellow-d)",
                borderBottom: "1px solid var(--line-1)",
            }}
        >
            {online ? <RotateCw size={14} /> : <WifiOff size={14} />}
            {online
                ? `${queued} ${queued === 1 ? "entry" : "entries"} queued · Syncing…`
                : "No signal · Working offline"}
        </div>
    );
}

function BottomTabs() {
    return (
        <nav
            data-testid={MOBILE.tabBar}
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-km grid grid-cols-4"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
            {tabs.map((t) => {
                const Icon = t.icon;
                return (
                    <NavLink
                        key={t.to}
                        to={t.to}
                        end={t.end}
                        data-testid={t.testid}
                        className={({ isActive }) =>
                            "flex flex-col items-center justify-center py-2.5 gap-0.5 text-[11px] transition-colors " +
                            (isActive ? "text-[color:var(--km-navy)] font-bold" : "text-[color:var(--ink-3)]")
                        }
                    >
                        <Icon size={20} strokeWidth={1.7} />
                        <span>{t.label}</span>
                    </NavLink>
                );
            })}
        </nav>
    );
}

function MobileHeader() {
    const { user } = useAuth();
    const now = new Date();
    const hour = now.getHours();
    const greet = hour < 12 ? "Morning" : hour < 17 ? "Arvo" : "Evening";
    const date = now.toLocaleDateString("en-AU", {
        timeZone: "Australia/Brisbane", weekday: "long", day: "numeric", month: "long",
    });
    return (
        <header className="px-5 pt-6 pb-4 bg-white">
            <div className="flex items-center justify-between mb-4">
                <img src="/logo.png" alt="KMSurveys" className="h-9 w-auto" draggable={false} />
                <InitialsAvatar name={user?.name} initials={user?.initials} size={38} />
            </div>
            <div className="flex items-baseline gap-2 mt-1">
                <h1 className="text-2xl font-extrabold tracking-[-0.03em]">{greet},</h1>
                <span className="text-2xl font-extrabold tracking-[-0.03em] text-[color:var(--km-navy)]">
                    {user?.name?.split(" ")[0] || "there"}
                </span>
            </div>
            <div className="text-sm text-muted mt-1">{date}</div>
        </header>
    );
}

function InnerShell() {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return null;
    if (!user) return <Navigate to="/login" replace state={{ from: location }} />;

    // show header only on top-level tab routes
    const showHeader = ["/m/today", "/m/jobs", "/m/week", "/m/profile"].some(
        (p) => location.pathname === p,
    );

    return (
        <div className="min-h-screen bg-[color:var(--surface-1)] pb-24">
            <OfflineBanner />
            {showHeader && <MobileHeader />}
            <main className="max-w-[560px] mx-auto">
                <Outlet />
            </main>
            <BottomTabs />
        </div>
    );
}

export function MobileShell() {
    return (
        <OfflineProvider>
            <InnerShell />
        </OfflineProvider>
    );
}
